// ═══════════════════════════════════════════════════════════════════════
// Frequency stats over a window of recent rounds — the numbers above the
// past-races list.
//
// Two shapes cover the whole fleet:
//   • crash  → multiplier buckets + the biggest hits and how long ago
//   • others → first-place frequency per marble (counting games included:
//              their outcome IS a marble named Under/Range/Over/Exact)
//
// These are DESCRIPTIVE stats over a short window, not published odds. Two
// rules keep them honest, and both are tested:
//   1. the denominator is rounds whose outcome actually parsed, never rounds
//      fetched — a settled round can legitimately arrive without its outcome
//      (older rounds lose their events to the 30-day prune), and dividing by
//      the fetched count biases every percentage toward zero, silently;
//   2. nothing here is ever labelled all-time. Callers render the sample size.
// ═══════════════════════════════════════════════════════════════════════

import { pipe, Array as A } from 'effect'
import type { Marble } from 'src/types/domain/Marble'
import { orderRacesByCreatedAt, type Race } from 'src/types/domain/Race'
import { resolveCrashEventInfo } from 'src/components/CrashChipPanel/crashEventInfo'
import { getLastWinners } from './raceUtils'
import { isRaceSettledEvent } from 'src/types/domain/RaceEvent'

const round1 = (n: number): number => Math.round(n * 10) / 10

const percent = (count: number, total: number): number =>
  total > 0 ? round1((count / total) * 100) : 0

// ── crash ────────────────────────────────────────────────────────────────

/** Half-open [min, max): a round that busts at exactly 2.00x pays 2x, so it
 *  belongs with the 2x-5x band, not with "under 2x". */
const CRASH_BUCKETS = [
  { key: 'under2', label: '< 2×', min: 0, max: 2 },
  { key: '2to5', label: '2×–5×', min: 2, max: 5 },
  { key: '5to10', label: '5×–10×', min: 5, max: 10 },
  { key: 'over10', label: '10×+', min: 10, max: Number.POSITIVE_INFINITY }
] as const

export type CrashBucketKey = (typeof CRASH_BUCKETS)[number]['key']

export type CrashBucket = Readonly<{
  key: CrashBucketKey
  label: string
  count: number
  pct: number
}>

export type CrashHighlight = Readonly<{
  raceId: string
  multiplier: number
  /** 0 = the most recent round in the window. */
  roundsAgo: number
}>

export type CrashStats = Readonly<{
  /** Rounds that contributed a multiplier — the denominator. */
  sampleSize: number
  buckets: CrashBucket[]
  highlights: CrashHighlight[]
}>

export const DEFAULT_HIGHLIGHT_MIN_MULTIPLIER = 10
export const DEFAULT_HIGHLIGHT_COUNT = 3

export const buildCrashStats = (
  races: readonly Race[],
  options: { highlightMinMultiplier?: number; highlightCount?: number } = {}
): CrashStats => {
  const {
    highlightMinMultiplier = DEFAULT_HIGHLIGHT_MIN_MULTIPLIER,
    highlightCount = DEFAULT_HIGHLIGHT_COUNT
  } = options

  // Newest first, so a round's index IS how many rounds ago it landed.
  const resolved = pipe(
    races,
    A.sortBy(orderRacesByCreatedAt),
    A.map((race, index) => ({
      raceId: race.uuid,
      multiplier: resolveCrashEventInfo(race.raceEvents).crashedMultiplier,
      roundsAgo: index
    })),
    A.filter(
      (entry): entry is { raceId: string; multiplier: number; roundsAgo: number } =>
        entry.multiplier !== undefined
    )
  )

  const sampleSize = resolved.length

  const buckets = CRASH_BUCKETS.map((bucket) => {
    const count = resolved.filter(
      (entry) => entry.multiplier >= bucket.min && entry.multiplier < bucket.max
    ).length

    return { key: bucket.key, label: bucket.label, count, pct: percent(count, sampleSize) }
  })

  const highlights = resolved
    .filter((entry) => entry.multiplier >= highlightMinMultiplier)
    .sort((a, b) => b.multiplier - a.multiplier)
    .slice(0, highlightCount)

  return { sampleSize, buckets, highlights }
}

// ── marble / counting ────────────────────────────────────────────────────

export type OutcomeStat = Readonly<{
  marble: Marble
  wins: number
  pct: number
}>

export type OutcomeStats = Readonly<{
  /** Rounds with a resolved first place — the denominator. */
  sampleSize: number
  outcomes: OutcomeStat[]
  /** Wins by marbles that did not fit in `outcomes`. Present ONLY when some
   *  were dropped — the rows must always account for the whole window, or the
   *  card shows percentages summing to less than 100 with nothing explaining
   *  the shortfall (Fast Lane's 10 lanes summed to ~84%). */
  other?: Readonly<{ wins: number; pct: number }>
}>

/** Above this many marbles, listing every one (most at 0% in a short window)
 *  is noise rather than information, so only the winners are shown. */
export const DEFAULT_OUTCOME_ROWS = 8

export const buildOutcomeStats = (
  races: readonly Race[],
  marbles: readonly Marble[],
  options: { maxRows?: number } = {}
): OutcomeStats => {
  const { maxRows = DEFAULT_OUTCOME_ROWS } = options

  const winners = getLastWinners([...races], [...marbles], races.length)
  const sampleSize = winners.length

  const wins = new Map<string, number>()
  for (const winner of winners) {
    wins.set(winner.marble.uuid, (wins.get(winner.marble.uuid) ?? 0) + 1)
  }

  // Retired marbles stay on a game's roster (Snake still carries Tiger and
  // Neptune) but cannot win, so a permanent 0% row is noise. One that DID win
  // inside the window has to stay, though — dropping it would lose a real
  // result and stop the rows adding up.
  const selectable = marbles.filter((m) => m.active || (wins.get(m.uuid) ?? 0) > 0)

  // A small roster shows every remaining marble, zeros included — an active
  // rare marble that happened not to win reads as variance, whereas omitting
  // it reads as "that marble isn't in this game". A large roster shows only
  // what actually won.
  const rows =
    selectable.length <= maxRows ? selectable : selectable.filter((m) => (wins.get(m.uuid) ?? 0) > 0)

  const ranked = rows
    .map((marble) => {
      const count = wins.get(marble.uuid) ?? 0
      return { marble, wins: count, pct: percent(count, sampleSize) }
    })
    .sort((a, b) => b.wins - a.wins || marbleOrder(a.marble) - marbleOrder(b.marble))

  const outcomes = ranked.slice(0, maxRows)
  const droppedWins = ranked.slice(maxRows).reduce((sum, o) => sum + o.wins, 0)

  // Percentage, not the sum of the dropped rows' percentages: rounding each
  // row to 1dp and then adding them drifts, and this figure exists precisely
  // so the card adds up.
  const other =
    droppedWins > 0 ? { wins: droppedWins, pct: percent(droppedWins, sampleSize) } : undefined

  return { sampleSize, outcomes, other }
}

// ── counting (CCTV) ──────────────────────────────────────────────────────

export type CountingTotal = Readonly<{
  /** Sum of every round's final count across the window. */
  total: number
  /** Rounds that actually contributed a count — the honest denominator. */
  rounds: number
  /** Engine's own label ('vehicles' | 'people' | 'ducks' | 'skiers' | …).
   *  Undefined when the rounds carry none, so the caller stays generic. */
  detectionType: string | undefined
}>

/**
 * "Total counted" across the window — 4,812 vehicles over the last 200 rounds.
 * Purely for colour; nothing bets on it.
 *
 * Counting games rotate across locations and a location change can bring a
 * different detector, so the label follows the window's MAJORITY detection
 * type rather than whichever round happens to sort first.
 */
export const buildCountingTotal = (races: readonly Race[]): CountingTotal | undefined => {
  let total = 0
  let rounds = 0
  const labels = new Map<string, number>()

  for (const race of races) {
    const settled = race.raceEvents.find(isRaceSettledEvent)
    if (!settled) continue

    const count = settled.payload.finalCount
    if (typeof count !== 'number' || !Number.isFinite(count) || count < 0) continue

    total += count
    rounds += 1

    const label = settled.payload.streamMetadata?.detectionType
    if (label) labels.set(label, (labels.get(label) ?? 0) + 1)
  }

  if (rounds === 0) return undefined

  const detectionType = [...labels.entries()].sort((a, b) => b[1] - a[1])[0]?.[0]

  return { total, rounds, detectionType }
}

/** Stable tie-break so equal-win marbles don't reshuffle between renders. */
const marbleOrder = (marble: Marble): number => {
  const parsed = Number.parseInt(marble.number, 10)
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER
}
